import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'

import ResponsiveImage from 'components/Common/ResponsiveImage'
import HeadData from 'components/Common/HeadData'
import config from 'constants/config'
import TextLink from 'LuxKit/TextLink'
import OhNoRefreshError from 'components/Common/ErrorBoundary/OhNoRefreshError'
import Sheet from 'components/utils/Sheet'

const Root = styled.div`
  position: relative;
  overflow: hidden;
  text-align: center;

  picture,
  img {
    height: calc(100vh - ${() => rem(config.header.height.lg)});
    object-fit: cover;
  }
`

const Wrapper = styled(Sheet)`
  position: absolute;
  z-index: 1;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const Main = styled.div`
  font-size: ${rem(140)};
`

const Secondary = styled.div`
  font-size: ${rem(30)};
`

const Links = styled.div`
  margin-top: ${rem(40)};
  font-size: ${rem(20)};
`

interface Props {
  error: any
  className?: string
}

function ErrorPage(props: Props) {
  const intl = useIntl()
  const { error, className } = props
  let description: string
  if (error.status && error.message) {
    description = intl.formatMessage(
      {
        id: 'sA+2GLKuip',
        defaultMessage: 'Error {errorStatus} - {errorMessage}',
        description: 'Error page description with status code and error message',
      },
      { errorStatus: error.status, errorMessage: error.message },
    )
  } else if (error.status) {
    description = intl.formatMessage(
      {
        id: 'pbYRcpXQ8l',
        defaultMessage: 'Error {errorStatus}',
        description: 'Error page description with status code only',
      },
      { errorStatus: error.status },
    )
  } else {
    description = intl.formatMessage({
      id: 'zJ/SQ8GVbI',
      defaultMessage: 'Error - Something went wrong',
      description: 'Fallback error page description when no status code is available',
    })
  }

  const handleBack = useCallback<React.MouseEventHandler<HTMLAnchorElement>>((event) => {
    event.preventDefault()
    window.history.back()
  }, [])

  return <Root className={clsx(`Error${error.status}Page)`, `T-Error${error.status}Page`, className)}>
    <HeadData
      title={description}
      description={description}
    />
    <ResponsiveImage
      aria-hidden="true"
      alt={description}
      brightness={-60}
      mobileAspectRatio="8:9"
      tabletAspectRatio="4:3"
      desktopAspectRatio="4:3"
      largeDesktopAspectRatio="16:9"
      id="404-background"
    />
    <Wrapper paletteType="inverse">
      {error.status && <>
        <Main>{error.status}</Main>
        <Secondary>{error.message || description}</Secondary>
        <Links>
          <FormattedMessage
            id="lvQClVvqoe"
            defaultMessage="Please go <backLink>Back</backLink> or head <homeLink>Home</homeLink>"
            description="Navigation help text with back and home links on error page"
            values={{
              backLink: (chunks: React.ReactNode) => <TextLink onClick={handleBack}>{chunks}</TextLink>,
              homeLink: (chunks: React.ReactNode) => <TextLink to="/">{chunks}</TextLink>,
            }}
          />
        </Links>
      </>}
      {!error.status && <OhNoRefreshError />}
    </Wrapper>
  </Root>
}

export default ErrorPage
